/**
 * Facebook history per customer (api/app/fb_history.py, docs/fb-history.md): the customer's older Messenger / Instagram
 * conversation, pulled once when they first write to us, stored as a read-only history chat.
 *   GET  /api/v1/contacts/:id/fb-history          status for the customer panel
 *   POST /api/v1/contacts/:id/fb-history/pull     "ดึงประวัติจาก Facebook อีกครั้ง" (owner/head)
 *   POST /api/v1/messages/:id/fb-media {idx}      an older picture/file → our storage now, or a polite reason
 *   GET/PUT /api/v1/fb-history/settings           the switch off / allowlist / on (owner writes)
 * Self-contained like ./devreset.ts (no change to the shared client): mock mode answers from the mock db.
 */
import { ApiError } from './client'
import { reportUnauthorized } from './http'
import { getApi, useMock } from './index'
import { MockChatApi } from './mock'
import type { ChatMessage, ConversationDetail, ThreadItem } from './types'

export type FbPullMode = 'off' | 'allowlist' | 'on'
export type FbJobStatus = 'queued' | 'running' | 'paused' | 'done' | 'failed'

export interface FbHistoryJob {
  status: FbJobStatus
  /** Code of the last stop: reached_end · details_refused · capped · no_thread · throttled · usage_high · no_token ·
   *  mode_off · graph_unavailable · permission · not_allowed · gave_up · list_rejected · no_live_chat · erased */
  reason: string | null
  trigger: 'first_contact' | 'manual'
  /** When a queued / paused job runs again (paused: Meta's wait or ours). */
  next_at: string | null
  finished_at: string | null
  imported: number
  listed: number
  meta_total: number | null
  /** Messages Meta would not give details for (docs: only the most recent ones have them). */
  refused: number
  /** History at or before this time could not be pulled. */
  refused_before: string | null
  reached_end: boolean
  capped: boolean
}

export interface FbHistoryStatus {
  supported: boolean
  channel?: 'fb' | 'ig'
  mode?: FbPullMode
  /** This chat may be pulled under the current switch (allowlist = test accounts only). */
  allowed?: boolean
  has_token?: boolean
  can_pull?: boolean
  live_conversation_id?: string | null
  job?: FbHistoryJob | null
  history?: { conversation_id: string; messages: number; first_at: string | null; last_at: string | null } | null
}

export interface FbHistorySettings {
  pull: FbPullMode
  can_write: boolean
}

async function call<T>(method: 'GET' | 'POST' | 'PUT', path: string, body?: unknown, timeoutMs = 15000): Promise<T> {
  const ctrl = new AbortController()
  const timer = setTimeout(() => ctrl.abort(), timeoutMs)
  const base = (import.meta.env.VITE_API_BASE ?? '').trim().replace(/\/+$/, '')
  try {
    const res = await fetch(new URL(`${base}/api/v1${path}`, window.location.origin).toString(), {
      method,
      headers: body === undefined ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: 'same-origin',
      signal: ctrl.signal,
    })
    if (res.status === 401) reportUnauthorized()
    if (!res.ok) {
      let detail = res.statusText
      try {
        const j = (await res.json()) as { detail?: unknown }
        if (typeof j.detail === 'string') detail = j.detail
      } catch {
        /* body not JSON */
      }
      throw new ApiError(res.status, detail || `HTTP ${res.status}`)
    }
    return (await res.json()) as T
  } catch (e) {
    if (e instanceof ApiError) throw e
    if (e instanceof DOMException && e.name === 'AbortError') throw new ApiError(0, 'หมดเวลารอเซิร์ฟเวอร์')
    throw new ApiError(0, 'ต่อเซิร์ฟเวอร์ไม่ได้')
  } finally {
    clearTimeout(timer)
  }
}

export interface FbHistoryApi {
  status(contactId: string): Promise<FbHistoryStatus>
  pull(contactId: string): Promise<void>
  media(messageId: string, idx: number): Promise<{ url: string; type: string | null }>
  settings(): Promise<FbHistorySettings>
  putSettings(pull: FbPullMode): Promise<FbHistorySettings>
}

const http: FbHistoryApi = {
  status: (id) => call('GET', `/contacts/${encodeURIComponent(id)}/fb-history`),
  pull: async (id) => {
    await call('POST', `/contacts/${encodeURIComponent(id)}/fb-history/pull`)
  },
  // a large video/file goes Meta → our storage inside this request
  media: (id, idx) => call('POST', `/messages/${encodeURIComponent(id)}/fb-media`, { idx }, 60000),
  settings: () => call('GET', '/fb-history/settings'),
  putSettings: (pull) => call('PUT', '/fb-history/settings', { pull }),
}

// ------------------------------------------------------------------ mock (VITE_USE_MOCK, screens tests)

const DAY = 86400000
const MOCK_HIST_SUFFIX = 'h'
/** Pim Chanok (mock c101, Messenger): a pulled history where Meta stopped giving details further back. */
const MOCK_RICH = 'c101'
const MOCK_PHOTO_OK =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600" viewBox="0 0 600 600">' +
      '<rect width="600" height="600" fill="#e9f3ec"/><rect x="120" y="140" width="360" height="320" rx="32" fill="#2f8a5b"/>' +
      '<circle cx="300" cy="280" r="70" fill="#f4c152"/></svg>',
  )

let mockMode: FbPullMode = 'allowlist'
const mockPulls = new Map<string, number>() // conversation id → time the owner pressed "ดึงอีกครั้ง"

function mockConvOfContact(api: MockChatApi, contactId: string): ConversationDetail | null {
  for (const c of api.db.conversations.values()) if (c.contact.id === contactId && !c.is_history) return c
  return null
}

function iso(ms: number): string {
  return new Date(ms).toISOString()
}

/** The history chat of mock c101, added to the mock db the first time someone opens it (kept out of the lists). */
function ensureMockHistory(api: MockChatApi, live: ConversationDetail): string {
  const id = `${live.id}${MOCK_HIST_SUFFIX}`
  if (api.db.conversations.has(id)) return id
  const now = api.db.now
  const base = now - 400 * DAY
  const detail: ConversationDetail = {
    ...structuredClone(live),
    id,
    status: 'closed',
    needs_reply: false,
    waiting_since: null,
    unread: 0,
    assigned_staff: null,
    last_message: null,
    last_message_at: null,
    follow_up_due_at: null,
    is_history: true,
    dev_resettable: false,
    ad_source: null,
  }
  api.db.conversations.set(id, detail)
  const who = { kind: 'contact' as const }
  const page = { kind: 'page' as const }
  const msg = (n: number, at: number, dir: 'in' | 'out', text: string | null, extra: Partial<ChatMessage> = {}): ThreadItem =>
    ({
      kind: 'message',
      id: `fbh${n}`,
      conversation_id: id,
      direction: dir,
      type: 'text',
      text,
      attachments: [],
      at: iso(at),
      author: dir === 'in' ? who : page,
      read: false,
      send: null,
      slip: null,
      ...extra,
    }) as ThreadItem
  const thread: ThreadItem[] = [
    msg(1, base, 'in', 'สวัสดีค่ะ สนใจแพ็กอาหารคลีน 7 วันค่ะ'),
    msg(2, base + 4 * 60000, 'out', 'สวัสดีค่ะ ขอบคุณที่สนใจนะคะ แพ็ก 7 วันมีทั้งแบบ 2 มื้อและ 3 มื้อค่ะ'),
    msg(3, base + 9 * 60000, 'in', null, {
      type: 'image',
      attachments: [{ type: 'image', url: null, name: 'photo.jpg', fetch: 'meta' }],
    }),
    msg(4, base + 11 * 60000, 'in', 'แบบนี้ส่งถึงคอนโดไหมคะ'),
    msg(5, base + 20 * 60000, 'out', 'ส่งได้ค่ะ รบกวนแจ้งที่อยู่กับเบอร์โทรด้วยนะคะ'),
    msg(6, now - 200 * DAY, 'in', 'ขอสั่งเหมือนรอบที่แล้วค่ะ ไม่เอากุ้งนะคะ'),
    msg(7, now - 200 * DAY + 3 * 60000, 'in', null, {
      type: 'image',
      attachments: [{ type: 'image', url: MOCK_PHOTO_OK, name: 'slip.jpg', fetch: 'meta' }],
    }),
    msg(8, now - 200 * DAY + 6 * 60000, 'out', 'ได้รับแล้วค่ะ ขอบคุณค่ะ'),
    msg(9, now - 30 * DAY, 'in', null, {
      type: 'file',
      attachments: [{ type: 'file', url: null, name: 'รายการแพ้อาหาร.pdf', fetch: 'meta' }],
    }),
  ]
  api.db.threads.set(id, thread)
  return id
}

function mockStatus(api: MockChatApi, contactId: string): FbHistoryStatus {
  const live = mockConvOfContact(api, contactId)
  if (!live || (live.channel !== 'fb' && live.channel !== 'ig')) return { supported: false }
  const now = api.db.now
  const canPull = mockMode !== 'off'
  const common = { supported: true, channel: live.channel, mode: mockMode, has_token: true, live_conversation_id: live.id }
  const pulled = mockPulls.get(live.id)
  if (pulled !== undefined && Date.now() - pulled < 2500) {
    return {
      ...common,
      allowed: true,
      can_pull: false,
      job: { status: 'running', reason: null, trigger: 'manual', next_at: null, finished_at: null, imported: 12, listed: 25,
        meta_total: 58, refused: 0, refused_before: null, reached_end: false, capped: false },
      history: null,
    }
  }
  if (live.id === MOCK_RICH) {
    return {
      ...common,
      allowed: true,
      can_pull: canPull,
      job: { status: 'done', reason: 'details_refused', trigger: 'first_contact', next_at: null, finished_at: iso(now - 2 * DAY),
        imported: 9, listed: 12, meta_total: 58, refused: 3, refused_before: iso(now - 420 * DAY), reached_end: false,
        capped: false },
      history: { conversation_id: `${live.id}${MOCK_HIST_SUFFIX}`, messages: 9, first_at: iso(now - 400 * DAY),
        last_at: iso(now - 30 * DAY) },
    }
  }
  // every other Messenger chat of the mock: not a test account → nothing pulled in test mode
  return { ...common, allowed: mockMode === 'on', can_pull: canPull && mockMode === 'on', job: null, history: null }
}

const mock: FbHistoryApi = {
  async status(contactId) {
    await new Promise((r) => setTimeout(r, 120))
    return mockStatus(getApi() as MockChatApi, contactId)
  },
  async pull(contactId) {
    await new Promise((r) => setTimeout(r, 200))
    const api = getApi() as MockChatApi
    const live = mockConvOfContact(api, contactId)
    if (!live) throw new ApiError(404, 'ไม่พบแชท Facebook ของลูกค้าคนนี้')
    if (mockMode === 'off') throw new ApiError(409, 'ปิดการดึงประวัติจาก Facebook อยู่ เปิดได้ที่ ตั้งค่า › ช่องทางที่เชื่อม')
    mockPulls.set(live.id, Date.now())
  },
  async media(messageId) {
    await new Promise((r) => setTimeout(r, 500))
    // mock: the old photo is still on Facebook, the PDF is not
    if (messageId === 'fbh3') return { url: MOCK_PHOTO_OK, type: 'image' }
    throw new ApiError(410, 'Facebook ไม่ให้ดึงไฟล์นี้แล้ว (ข้อความเก่าเกินกว่าที่ Meta เปิดให้ดึง)')
  },
  async settings() {
    const api = getApi() as MockChatApi
    return { pull: mockMode, can_write: api.db.meId === 'ploy' }
  },
  async putSettings(pull) {
    await new Promise((r) => setTimeout(r, 150))
    mockMode = pull
    return { pull, can_write: true }
  },
}

/** Tests inject their own. */
let impl: FbHistoryApi | null = null
export function setFbHistoryApi(api: FbHistoryApi | null): void {
  impl = api
}

export function fbHistoryApi(): FbHistoryApi {
  if (impl) return impl
  return useMock && getApi() instanceof MockChatApi ? mock : http
}

/**
 * Before opening a history chat: in mock mode it is added to the mock db only now (so the mock lists stay as they
 * were until someone opens it). The real API already has it.
 */
export function prepareHistoryChat(liveId: string): void {
  const api = getApi()
  if (impl || !useMock || !(api instanceof MockChatApi)) return
  const live = api.db.conversations.get(liveId)
  if (live) ensureMockHistory(api, live)
}
